import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Form as FormikForm } from "formik";

import { PROPERTY_PANEL_INLINE_LABEL_WIDTH, transientOptions } from "../utilities";
import { useFormContext } from "./FormContext";
export { FieldArray } from "formik";

type LabelAlignment = "inline" | "stacked";

// Published as variables rather than styling the rows directly, so a row can override its own
// layout without having to out-specify the form.
const StyledForm = styled(FormikForm, transientOptions)<{
  $rowSpacing?: number;
  $labelAlignment?: LabelAlignment;
  $labelWidth?: number;
}>`
  --form-row-spacing: ${({ $rowSpacing }) => $rowSpacing ?? 15}px;

  ${({ $labelAlignment, $labelWidth }) =>
    $labelAlignment === "inline" &&
    css`
      --form-row-display: grid;
      --form-row-label-columns: ${$labelWidth ?? PROPERTY_PANEL_INLINE_LABEL_WIDTH}px 1fr;
      --form-row-label-align: center;
    `};
`;

type Props = Parameters<typeof StyledForm>[0] & {
  blurOnEnter?: boolean;
};

export const Form = ({ onKeyDown, children, blurOnEnter = true, ...props }: Props) => {
  const { dataTest } = useFormContext();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    onKeyDown?.(e);

    const target = e.target as HTMLElement;
    // In autocomplete/multi-select inputs Enter adds an item; keep focus so the
    // user can keep typing. The control commits its own value via onChange.
    const isAutocompleteInput = target.getAttribute?.("aria-autocomplete") != null;

    // Allow users to add a newline in a textarea with Shift+Enter
    if (blurOnEnter && e.key === "Enter" && !e.shiftKey && !isAutocompleteInput) {
      e.stopPropagation();
      (target as HTMLInputElement).blur?.();
    }
  };

  return (
    <StyledForm
      {...props}
      data-test={dataTest}
      onKeyDown={handleKeyDown}
      autoComplete={props.autoComplete ?? "off"}
      autoSave={props.autoSave ?? "off"}
      autoCorrect={props.autoCorrect ?? "off"}
      spellCheck={props.spellCheck ?? "false"}
    >
      {children}
    </StyledForm>
  );
};
